import { DataManager } from '@syncfusion/ej2-data';
import { Subject, catchError, debounceTime, of, switchMap, take, takeUntil } from 'rxjs';
import { TankFilterService, LastMovedOnFilterModel } from '@services/tank-filter.service';
import { SecurityPolicies } from '@services/auth-variables';
import {
  Component,
  AfterViewInit,
  ViewChild,
  Inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  Optional,
  Input,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ListBoxComponent } from '@syncfusion/ej2-angular-dropdowns';
import { ITankMoveListItem } from '@interfaces/tankMoveListItem';
import {
  IMoveTankTreeViewData,
  MoveTankFormModes,
  MoveTankService,
  TreeNode,
} from './services/move-tank.service';
import { TankService } from '@services/tank.service';
import { ILocationChangedToModel } from '@components/move-to-location-form/move-to-location-form.component';
import { AuthService } from '@services/auth.service';
import { Router } from '@angular/router';
import { deviceTypeCategory } from '../../interfaces/deviceTypeCategory';
import { AppDialogService } from '@services/app-dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogService } from 'src/app/modules/confirmation-dialog/confirmation-dialog.service';
import { DualTreeViewComponent } from '@components/dual-treeview/dual-treeview.component';
import { FormIssue } from '@interfaces/form-issue';

type icons = 'org' | 'loc' | 'tank';

@Component({
  selector: 'app-tank-move',
  templateUrl: './tank-move.component.html',
  styleUrls: ['./tank-move.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TankMoveComponent implements AfterViewInit, OnDestroy {
  @ViewChild('listboxB') listboxTo: ListBoxComponent;
  @ViewChild('listboxA') listboxFrom: ListBoxComponent;
  @ViewChild('dualTreeView') dualTreeView: DualTreeViewComponent;
  securityPolicies = SecurityPolicies;
  selectedTanks: ITankMoveListItem[] = [];
  associatedTanks: ITankMoveListItem[] = [];
  organizations: any[] = [];

  /**
   * One sentence per group of tanks whose affiliation the pending move would change, shown at the
   * bottom of the form. Empty when nothing would change, which hides the section.
   *
   * Same shape and the same sentences as Move Locations, which does the same write - see
   * location-move.component. The consequence belongs on the form, where the destination can still be
   * changed; the MOVE dialog states the rule beside its permissions and rate-plan warnings.
   */
  affiliationPreviewLines: FormIssue[] = [];

  private readonly affiliationPreviewRefresh$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  fields = {
    text: 'name',
    value: 'tankId',
  };
  toolbarSettings = {
    items: ['moveTo', 'moveFrom', 'moveAllTo', 'moveAllFrom'],
  };

  uniqueId: number | null = this.data?.uniqueId;
  locationId: number | null = this.data?.locationId;
  organizationId: number | null = this.data?.organizationId;
  billableOrgId: number | null = this.data?.billableOrganizationId;
  tankSourceOrganizationId: number | null = this.data?.tankSourceOrganizationId;
  onMoveCompleteCallback: () => void = this.data?.onMoveCompleteCallback;

  isLoading: boolean = true;
  hasAssociatedTanks: boolean = false;
  locationFormVM: { billableOrgId: any; orgId: any; locationId: any; enableLocationForm?: boolean };
  moveInProgress: boolean = false;
  loadingChildData: boolean = false;

  treeViewData: TreeNode[] = null;
  checkedTreeNodes: any;
  selectedTanksNodes: TreeNode[];
  selectedLocationNodes: TreeNode[];
  selectedTankCount: number = 0;
  treeViewDataManager: DataManager;
  formMode: MoveTankFormModes = this.data?.formMode;

  get isMoveButtonEnabled() {
    return this.locationSelected && this.hasTanksToMove && !this.moveInProgress;
  }

  get hasTanksToMove(): boolean {
    return (
      (this.dualTreeView?.rightDataSource?.length ?? 0 + (this.selectedTanks?.length ?? 0)) +
        (this.selectedLocationNodes?.length ?? 0) +
        (this.selectedTanksNodes?.length ?? 0) >
      0
    );
  }

  get locationSelected(): boolean {
    //TODO: Need to refactor to support Skytank sensor/gateway logic.

    return this.formMode === 'tank'
      ? this.selectedLocationItem?.location?.value !== this.locationFormVM?.locationId &&
          this.selectedLocationItem?.location?.value > 0
      : this.selectedLocationItem !== null && this.selectedLocationItem.location?.value > 0;
  }

  get formIsBusy(): boolean {
    return this.isLoading || this.moveInProgress || this.loadingChildData;
  }

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    @Optional() private dialogRef: MatDialogRef<TankMoveComponent>,
    private changeDetectorRef: ChangeDetectorRef,
    private moveTankService: MoveTankService,
    private tankService: TankService,
    private authService: AuthService,
    private tankFilterService: TankFilterService,
    private route: Router,
    private appDialogService: AppDialogService,
    private translate: TranslateService,
    private confirmationDialogService: ConfirmationDialogService
  ) {
    this.initializeForm();
    this.watchAffiliationPreview();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectedLocationItem: ILocationChangedToModel = null;

  get batchLoadLocationsFn(): (
    orgIds: string[]
  ) => Promise<{ organizations: any[]; locations: any[]; tanks: any[] }> {
    return (orgIds: string[]) => {
      this.loadingChildData = true;
      this.changeDetectorRef.markForCheck();
      return this.moveTankService
        .getLocationsForOrganizations(
          orgIds.map(id => parseInt(id.replace(/^o/, ''), 10)),
          this.locationId ?? undefined
        )
        .then(result => {
          return result;
        })
        .finally(() => {
          this.loadingChildData = false;
          this.changeDetectorRef.markForCheck();
        });
    };
  }

  get batchLoadTanksFn(): (locationIds: string[]) => Promise<{ tanks: any[] }> {
    return (locationIds: string[]) => {
      const locIds = locationIds
        .map(id => parseInt(id.replace(/^l/, ''), 10))
        .filter(n => !Number.isNaN(n));
      if (locIds.length === 0) return Promise.resolve({ tanks: [] });
      this.loadingChildData = true;
      this.changeDetectorRef.markForCheck();
      return this.moveTankService
        .getTanksForLocationIds(this.tankSourceOrganizationId, locIds)
        .then(result => {
          return result;
        })
        .finally(() => {
          this.loadingChildData = false;
          this.changeDetectorRef.markForCheck();          
        });
    };
  }

  get loadReceivingLocationsOnlyFn(): () => Promise<{ organizations: any[]; locations: any[] }> {
    const orgId = this.organizationId ?? this.tankSourceOrganizationId;
    return () => {
      this.loadingChildData = true;
      this.changeDetectorRef.markForCheck();
      return orgId != null && !Number.isNaN(orgId)
        ? this.moveTankService.getReceivingLocationsOnly(orgId).then(result => {
            return result;
          }).finally(() => {
            this.loadingChildData = false;
            this.changeDetectorRef.markForCheck();            
          })
        : Promise.resolve({ organizations: [], locations: [] });
    };
  }

  ngAfterViewInit(): void {
    this.disablePrimaryTanks();
    this.changeDetectorRef.detectChanges();
  }

  onCloseButtonClick() {
    this.dialogRef.close();
  }

  onCancelButtonClick() {
    this.dialogRef.close();
  }

  onMoveButtonClick() {
    this.moveTanks();
  }

  onActionBegin(args) {
    if (
      args.eventName !== 'moveAllTo' &&
      args.eventName !== 'moveAllFrom' &&
      args.eventName !== 'moveTo' &&
      args.eventName !== 'moveFrom'
    )
      return;

    args.cancel = true;
    if (!args.items) return;

    let itemsToMove = args.items.filter(item => item?.isAssociatedTank === true);

    if (itemsToMove.length === 0) return;

    setTimeout(() => {
      this.manualMoveItemsFrom(
        itemsToMove,
        args?.eventName === 'moveAllTo' || args?.eventName === 'moveTo'
      );
    }, 100);
  }

  onActionComplete(args) {
    this.disablePrimaryTanks();
  }

  onLocationChanged(selectedLocationItem: ILocationChangedToModel) {
    this.selectedLocationItem = this.getSelectedLocationItem(selectedLocationItem);
    //The destination decides the affiliation, so this is the change that alters the answer most.
    this.affiliationPreviewRefresh$.next();
    this.changeDetectorRef.markForCheck();
  }

  onDualTreeView_RightChanges(data: TreeNode[]) {
    this.selectedTanksNodes = data.filter(d => d.level === 3);
    this.selectedLocationNodes = data.filter(
      d => d.level === 2 && !this.selectedTanksNodes.some(t => t.parentId === d.id)
    );
    this.selectedTankCount = this.selectedTanksNodes.length;
    this.affiliationPreviewRefresh$.next();
    this.changeDetectorRef.detectChanges();
  }

  onTreeDataLoading() {
    this.isLoading = true;
  }

  onTreeDataLoaded() {
    this.isLoading = false;
  }

  private manualMoveItemsFrom(itemsToMove: any[], isMoveTo: boolean) {
    // Move each item individually
    if (isMoveTo) {
      //move from associated to selected arrays
      itemsToMove.forEach(item => {
        this.associatedTanks = this.associatedTanks.filter(st => st.tankId !== item.tankId);
        this.selectedTanks.push(item);
      });
    } else {
      //move from selected to associated arrays
      itemsToMove.forEach(item => {
        this.selectedTanks = this.selectedTanks.filter(st => st.tankId !== item.tankId);
        this.associatedTanks.push(item);
      });
    }

    this.listboxFrom.refresh();
    this.listboxTo.refresh();

    if (isMoveTo && this.associatedTanks.length > 0) {
      this.listboxFrom.selectItems([this.associatedTanks[0].tankId.toString()], true, true);
    } else {
      if (this.selectedTanks?.filter(s => s.isAssociatedTank).length > 0)
        this.listboxTo?.selectItems(
          [
            this.selectedTanks[
              this.selectedTanks.filter(s => !s.isAssociatedTank).length
            ].tankId.toString(),
          ],
          true,
          true
        );
    }
    this.disablePrimaryTanks();
    //The associated-tank path moves items between the two listboxes rather than through the tree, so
    //it is the only other thing that changes which tanks the preview is counting.
    this.affiliationPreviewRefresh$.next();
  }

  private disablePrimaryTanks() {
    // Disable the primary tanks in the "To" listbox
    let primaryTankIds: any = this.selectedTanks.filter(st => st.isAssociatedTank === false);
    if (primaryTankIds.length !== 0) {
      primaryTankIds = primaryTankIds.map(st => st.tankId.toString());
      setTimeout(() => {
        this.listboxTo.enableItems(primaryTankIds, false, true);
        this.changeDetectorRef.detectChanges();
      }, 100);
    }
  }

  private initializeForm() {
    this.dialogRef.disableClose = true; //ensure the user has to click close

    switch (this.formMode) {
      case 'tank':
        this.initializeAssociatedTankView();
        break;
      case 'organization':
        this.initializeOrgSelectedView();
        break;
      case 'location':
        this.initializeMoveToLocationView();
        break;
    }
  }

  public mapToTreeNode(result: any): TreeNode[] {
    const data: IMoveTankTreeViewData = result['result'];
    const addAdditionalProperties = (
      node: TreeNode,
      identifyer: string,
      parentIdentifyer: string,
      level: number,
      hasChildren: boolean,
      icon: string,
      childrenAreLoaded?: boolean,
      deviceCategory?: deviceTypeCategory
    ): TreeNode => {
      node.id = identifyer + node.id;
      node.level = level;

      if (node.parentId) node.parentId = parentIdentifyer + node.parentId;

      if (hasChildren) node.hasChildren = true;

      if (icon) node.icon = icon;

      //top level children are always loaded
      if (childrenAreLoaded) {
        node.areChildrenLoaded = true;
      }

      if (node.dtc) node.dtc = deviceCategory;
      return node;
    };

    return [
      ...data.organizations.map(org =>
        addAdditionalProperties(
          org,
          'o',
          null,
          1,
          true,
          'org'
          //true
        )
      ),
      ...data.locations.map(loc => addAdditionalProperties(loc, 'l', 'o', 2, true, 'loc')),
      ...data.tanks.map(tank => {
        //if tank device category is skytank gateway, set icon to skytank-gateway
        const icon = tank.dtc === deviceTypeCategory.skyTankGateway ? 'skytank-gateway' : 'tank';
        return addAdditionalProperties(tank, 't', 'l', 3, false, icon);
      }),
    ];
  }

  private initializeMoveToLocationView() {
    this.treeViewDataManager = this.moveTankService.getTanksToMoveTreeViewDataManager(
      this.organizationId,
      this.locationId
    );

    this.locationFormVM = {
      billableOrgId: this.billableOrgId,
      orgId: this.organizationId,
      locationId: this.locationId,
      enableLocationForm: true,
    };
  }

  private initializeOrgSelectedView() {
    this.treeViewDataManager = this.moveTankService.getTanksToMoveTreeViewDataManager(
      this.tankSourceOrganizationId,
      null
    );

    this.locationFormVM = {
      billableOrgId: this.billableOrgId,
      orgId: this.organizationId,
      locationId: null,
      enableLocationForm: true,
    };
  }

  private initializeAssociatedTankView() {
    this.moveTankService
      .getTanksToMoveLists([this.uniqueId])
      .pipe(take(1))
      .subscribe(tanks => {
        this.associatedTanks = tanks.associatedTanks;
        this.selectedTanks = tanks.selectedTanks;
        if (this.associatedTanks && this.associatedTanks.length > 0) {
          this.hasAssociatedTanks = true;

          //skytank logic...
          //If Tank to Move is a skytank sensor and has a skytank gateway associated with it
          const selectedTank = this.selectedTanks[0];
          const skytank = this.associatedTanks.find(
            t => t.deviceCategory === deviceTypeCategory.skyTankGateway
          );
          if (selectedTank.deviceCategory === deviceTypeCategory.skyTankSensor) {
            if (skytank && skytank.locationId) {
              this.locationFormVM = {
                billableOrgId: skytank.billableOrgId,
                orgId: skytank.orgId,
                locationId: skytank.locationId,
                enableLocationForm:
                  selectedTank.isLocationShipTo && selectedTank.locationId !== skytank.locationId,
              };
            }
            this.isLoading = false;
            this.changeDetectorRef.detectChanges();
            return;
          }
        }

        //setup BillOrg|Org|Location form control values
        this.locationFormVM = {
          billableOrgId: this.selectedTanks[0]?.billableOrgId,
          orgId: this.selectedTanks[0]?.orgId,
          locationId: this.selectedTanks[0]?.locationId,
          enableLocationForm: true,
        };

        this.isLoading = false;
        this.changeDetectorRef.detectChanges();
      });
  }

  /**
   * The tanks the move would actually write, from whichever of the two selection surfaces this form
   * is using: the dual tree in 'location' and 'organization' mode, the dual listbox in 'tank' mode.
   *
   * Shared by the preview and by {@link moveTanks} so the sentences on the form cannot describe a
   * different set from the one that gets moved. The mapping used to live inside moveTanks, where the
   * preview had no way to reach it.
   */
  private resolveTanksToMove(): ITankMoveListItem[] {
    if (this.selectedTanksNodes && this.selectedTanksNodes.length > 0) {
      return this.selectedTanksNodes.map(node => {
        return {
          tankId: parseInt(node.id.replace('t', ''), 10),
          name: node.name,
        };
      }) as unknown as ITankMoveListItem[];
    }

    return this.selectedTanks ?? [];
  }

  /**
   * Keeps the inline preview in step with the selected tanks and the destination. Every change asks
   * again, and switchMap drops an answer that is already stale, so a quick run of changes cannot
   * leave an older count on screen.
   *
   * Debounced, which Move Locations does not need to be: one gesture on the tree can check a whole
   * organization, and the request body carries every tank id in the selection.
   */
  private watchAffiliationPreview(): void {
    this.affiliationPreviewRefresh$
      .pipe(
        debounceTime(200),
        switchMap(() => {
          const tanks = this.resolveTanksToMove();
          const destinationLocationId = this.selectedLocationItem?.location?.value;

          return tanks.length === 0 || !(destinationLocationId > 0)
            ? of(null)
            : this.moveTankService
                .getAffiliationPreview(tanks, destinationLocationId)
                // The preview informs; a failed fetch hides it rather than ending the stream, and
                // the move asks the server for the real answer regardless.
                .pipe(catchError(() => of(null)));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(preview => {
        this.affiliationPreviewLines = preview?.hasAffiliationChanges
          ? this.confirmationDialogService.describeMoveAffiliationChanges(preview)
          : [];
        this.changeDetectorRef.markForCheck();
      });
  }

  private moveTanks() {
    if (this.moveInProgress === true) return;

    this.moveInProgress = true;
    document.body.style.cursor = 'wait';
    this.changeDetectorRef.detectChanges();

    //if associated tanks are not being moved - display a simple warning
    of(null)
      .pipe(
        take(1),
        switchMap(() => {
          if (this.associatedTanks.length > 0) {
            return this.appDialogService.showGeneralDialog(
              '',
              this.translate.instant('MoveForms.TankMove.AssociatedTanksWarning')
            );
          }
          return of(null);
        }),
        switchMap(() => {
          if (this.dualTreeView?.rightDataSource?.length > 0) {
            this.selectedTanksNodes = this.dualTreeView.rightDataSource.filter(d => d.level === 3);
          }

          //dual-tree (bulk) or list-view(selected tank + associated tanks)
          //  this.selectedTanksNodes = dual-tree
          // this.selectedTanks = list-view
          this.selectedTanks = this.resolveTanksToMove();

          // The affiliation counts are on the FORM, in the alert above these buttons, kept in step
          // with the selection - not asked for here as a second dialog in front of this one. The
          // move re-resolves every tank's affiliation from the destination organization, so a tank
          // an owner deliberately unshared is re-shared by a move inside their own organization; the
          // dialog below states that rule, beside its permissions and rate-plan warnings.
          return this.moveTankService.showConfirmationDialog().pipe(
            switchMap(result => {
              if (!result) return of(null);
              if (this.selectedTanks && this.selectedTanks.length > 0) {
                return this.moveTankService.moveTanks(
                  this.selectedTanks,
                  this.selectedLocationItem?.location?.value
                );
              }
              return of(null);
            }),
            take(1)
          );
        })
      )
      .subscribe({
        next: result => {
          if (!result) return;
          //close Move Tank Form Dialog Pop-uip
          this.dialogRef.close();

          //show success move tank Dialog pop-up
          this.showMoveStatusDialog(result);
        },
        complete: () => {
          this.moveInProgress = false;
          document.body.style.cursor = 'default';
          this.changeDetectorRef.detectChanges();
        },
      });
  }

  private showMoveStatusDialog(result: any) {
    this.moveTankService
      .showMoveStatusDialog(result)
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        this.dialogRef.close();

        if (this.locationId) this.onMoveCompleteCallback();

        of(null)
          .pipe(
            switchMap(() => {
              //if org changed - change user's CSO.
              //This has to settle before the tank is re-read: the tank now belongs to the
              //receiving org, and re-reading it under the previous CSO fails as out of scope.
              if (this.selectedLocationItem.orgChanged === true) {
                return this.authService.updateLastSelectedOrganization(
                  this.selectedLocationItem.organizationId
                );
              }
              return of(null);
            }),
            take(1)
          )
          .subscribe(() => {
            //send user back to tank list
            if (result?.sendToList === true) {
              //leaving the tank page - just drop the stale cache, no need to re-read it
              if (this.uniqueId) this.tankService.invalidateKeyTankInfo();

              //set the Filter Model to 'Moved Tanks'
              const movedTanksFilter =
                this.tankFilterService.createTankFilterModel(LastMovedOnFilterModel);
              this.tankFilterService.applyFilterModelToState(
                movedTanksFilter,
                this.constructor.name,
                true
              );
              this.route.navigate(['tank']);
              return;
            }

            //staying on the tank page - refresh tank details so the page picks up the tank's
            //new location/organization
            if (this.uniqueId) this.tankService.reloadKeyTankInfo();
          });
      });
  }

  private getSelectedLocationItem(
    selectedLocationItem: ILocationChangedToModel
  ): ILocationChangedToModel {
    return this.formMode === 'tank'
      ? this.locationFormVM.locationId !== selectedLocationItem?.location?.value
        ? selectedLocationItem
        : null
      : selectedLocationItem;
  }
}
